import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";

export function GoodLeapSettings() {
  const { orgId } = useAuth();
  const [partnerUrl, setPartnerUrl] = useState("");
  const [appScheme, setAppScheme] = useState("goodleap://");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const { data } = await supabase
        .from("organizations")
        .select("goodleap_partner_url, goodleap_app_scheme")
        .eq("id", orgId)
        .single();
      if (data) {
        setPartnerUrl(data.goodleap_partner_url || "");
        setAppScheme(data.goodleap_app_scheme || "goodleap://");
      }
      setLoading(false);
    })();
  }, [orgId]);

  const save = async () => {
    if (!orgId) return;
    setSaving(true);
    const { error } = await supabase
      .from("organizations")
      .update({
        goodleap_partner_url: partnerUrl || null,
        goodleap_app_scheme: appScheme || "goodleap://",
      })
      .eq("id", orgId);
    setSaving(false);
    if (error) {
      toast.error("Failed to save");
      return;
    }
    toast.success("GoodLeap settings saved");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          GoodLeap Financing
        </CardTitle>
        <CardDescription>
          Configure the GoodLeap partner link and mobile app deep link used by the “Run Financing” button on jobs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="gl-url">Partner URL</Label>
          <Input
            id="gl-url"
            placeholder="https://www.goodleap.dev/yourpartner/..."
            value={partnerUrl}
            onChange={(e) => setPartnerUrl(e.target.value)}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            Web fallback used when the GoodLeap app isn't installed.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="gl-scheme">App Deep Link Scheme</Label>
          <Input
            id="gl-scheme"
            placeholder="goodleap://"
            value={appScheme}
            onChange={(e) => setAppScheme(e.target.value)}
            disabled={loading}
          />
        </div>
        <Button onClick={save} disabled={saving || loading}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}
